import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { RoleLevel } from "../types/job";
import { ROLE_LEVEL_OPTIONS } from "../lib/roleLevel";

type RoleLevelOnboardingModalProps = {
  open: boolean;
  onSelect: (level: RoleLevel) => void;
};

export function RoleLevelOnboardingModal({
  open,
  onSelect,
}: RoleLevelOnboardingModalProps) {
  const titleId = useId();
  const descId = useId();
  const firstRadioRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<RoleLevel | null>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    firstRadioRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") e.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-end justify-center p-4 sm:items-center sm:p-6"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
      >
        <h2
          id={titleId}
          className="text-lg font-semibold tracking-tight text-slate-900"
        >
          What roles are you looking for?
        </h2>
        <p id={descId} className="mt-2 text-sm leading-relaxed text-slate-600">
          Pick one focus. We’ll filter listings by job title keywords. You can change this anytime
          from the filter icon next to the job count.
        </p>

        <fieldset className="mt-5 space-y-2.5">
          <legend className="sr-only">Role level</legend>
          {ROLE_LEVEL_OPTIONS.map((opt, i) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition focus-within:ring-2 focus-within:ring-primary/40 ${
                selected === opt.value
                  ? "border-primary bg-primary/5"
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/80"
              }`}
            >
              <input
                ref={i === 0 ? firstRadioRef : undefined}
                type="radio"
                name="onboarding-role"
                value={opt.value}
                checked={selected === opt.value}
                onChange={() => setSelected(opt.value)}
                className="mt-0.5 size-4 shrink-0 border-slate-300 text-primary focus:ring-primary/30"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">
                  {opt.label}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-slate-500">
                  {opt.description}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        <button
          type="button"
          disabled={selected === null}
          onClick={() => selected && onSelect(selected)}
          className="mt-6 flex h-11 w-full items-center justify-center rounded-xl bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </div>,
    document.body,
  );
}
